import { observeMatchingElements } from './observeMatchingElements';
const HERO_SELECTOR = '.settings-hero';
const META_SELECTOR = '.settings-hero__meta';
const RESPONSIVE_TAB_STRIP_SELECTOR = [
  '.settings-hero__tabs',
  '.library-toolbar__tabs',
  '.library-toolbar__subtabs',
  '.tabs',
  '.tabs-bar',
  '.attached-tabs.horizontal',
  '.admin-service-tabs',
  '.labor-calendar-scheduler__surface-tabs',
  '[role="tablist"]:not([aria-orientation="vertical"])',
].join(',');
// Scrollers that hold a tab strip without being one (the order workspace's connected date
// tabs: the strip is max-content and its wrapper scrolls). They only get the overflow marks.
const TAB_SCROLLER_SELECTOR = '.order-workspace-room-date-tabs--connected';

const enhancedMeta = new WeakSet<HTMLElement>();
const enhancedTabStrips = new WeakSet<HTMLElement>();
const trackedOverflow = new WeakSet<HTMLElement>();

let installed = false;
let nextId = 0;

export function installHeroStatsToggleEnhancer(): void {
  if (installed) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;

  observeMatchingElements(`${HERO_SELECTOR},${RESPONSIVE_TAB_STRIP_SELECTOR},${TAB_SCROLLER_SELECTOR}`, element => {
    if (element.matches(HERO_SELECTOR)) enhanceHero(element);
    if (element.matches(RESPONSIVE_TAB_STRIP_SELECTOR)) enhanceTabStrip(element);
    if (element.matches(TAB_SCROLLER_SELECTOR)) trackTabStripOverflow(element);
  });
}

function enhanceHero(hero: HTMLElement): void {
  const metas = Array.from(hero.querySelectorAll<HTMLElement>(META_SELECTOR));
  metas.forEach(meta => enhanceMeta(meta, hero));
}

function enhanceMeta(meta: HTMLElement, hero: HTMLElement): void {
  if (enhancedMeta.has(meta)) {
    const previous = meta.previousElementSibling;
    if (previous instanceof HTMLButtonElement && previous.classList.contains('settings-hero__meta-toggle')) {
      setExpanded(meta, previous, meta.dataset.mobileExpanded === 'true', hero);
      return;
    }
    enhancedMeta.delete(meta);
  }
  if (!meta.isConnected) return;

  const id = ensureMetaId(meta);
  const button = document.createElement('button');
  const label = document.createElement('span');
  const icon = document.createElement('span');

  button.type = 'button';
  button.className = 'settings-hero__meta-toggle';
  button.setAttribute('aria-controls', id);
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-label', 'Show stats');

  label.className = 'settings-hero__meta-toggle-label';
  label.textContent = visibleToggleLabel(meta, false);

  icon.className = 'settings-hero__meta-toggle-icon';
  icon.setAttribute('aria-hidden', 'true');

  button.append(label, icon);
  button.addEventListener('click', () => {
    const expanded = meta.dataset.mobileExpanded === 'true';
    setExpanded(meta, button, !expanded, hero);
  });

  setExpanded(meta, button, false, hero);
  meta.before(button);
  enhancedMeta.add(meta);
}

/** A hero can ask for a shorter visible label with `data-stats-toggle-label` (e.g. "Stats");
 * the button's accessible name still says "Show stats" or "Hide stats". */
function visibleToggleLabel(meta: HTMLElement, expanded: boolean): string {
  return meta.dataset.statsToggleLabel?.trim() || (expanded ? 'Hide stats' : 'Show stats');
}

function ensureMetaId(meta: HTMLElement): string {
  if (meta.id) return meta.id;
  nextId += 1;
  meta.id = `settings-hero-stats-${nextId}`;
  return meta.id;
}

function setExpanded(
  meta: HTMLElement,
  button: HTMLButtonElement,
  expanded: boolean,
  hero: HTMLElement,
): void {
  meta.dataset.mobileExpanded = expanded ? 'true' : 'false';
  button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  button.setAttribute('aria-label', expanded ? 'Hide stats' : 'Show stats');

  const label = button.querySelector<HTMLElement>('.settings-hero__meta-toggle-label');
  const labelText = visibleToggleLabel(meta, expanded);
  if (label && label.textContent !== labelText) label.textContent = labelText;

  getStatsTargets(hero).forEach((target) => {
    target.dataset.mobileExpanded = expanded ? 'true' : 'false';
  });
}

function getStatsTargets(hero: HTMLElement): HTMLElement[] {
  const rawTargets = hero.dataset.statsTargets ?? '';
  if (!rawTargets.trim()) return [];
  return rawTargets
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => document.getElementById(id))
    .filter((target): target is HTMLElement => target instanceof HTMLElement);
}

function enhanceTabStrip(strip: HTMLElement): void {
  strip.dataset.responsiveTabStrip = 'true';
  if (enhancedTabStrips.has(strip)) return;

  strip.addEventListener('click', (event) => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLElement>('button, a, [role="tab"]')
      : null;
    if (!target || !strip.contains(target)) return;
    window.setTimeout(() => scrollActiveTabIntoView(strip, target), 0);
  });

  enhancedTabStrips.add(strip);
  trackTabStripOverflow(strip);
  window.setTimeout(() => scrollActiveTabIntoView(strip), 0);
}

/**
 * Marks which ends of a scrolling strip hide tabs (`data-overflow-start` / `data-overflow-end`),
 * so the phone CSS fades only toward tabs that are out of view. A strip that fits keeps its ends:
 * the old always-on fade dissolved rounded tracks, borders and the first tab's selected fill.
 */
function trackTabStripOverflow(strip: HTMLElement): void {
  if (typeof ResizeObserver === 'undefined' || trackedOverflow.has(strip)) return;
  trackedOverflow.add(strip);
  let frame = 0;
  const update = () => {
    frame = 0;
    if (!strip.isConnected) {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      return;
    }
    const maxScroll = strip.scrollWidth - strip.clientWidth;
    setFlag(strip, 'overflowStart', maxScroll > 1 && strip.scrollLeft > 1);
    setFlag(strip, 'overflowEnd', maxScroll > 1 && strip.scrollLeft < maxScroll - 1);
  };
  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };
  const resizeObserver = new ResizeObserver(schedule);
  const observeChildren = () => {
    resizeObserver.observe(strip);
    Array.from(strip.children).forEach(child => resizeObserver.observe(child));
  };
  const mutationObserver = new MutationObserver(() => {
    observeChildren();
    schedule();
  });
  observeChildren();
  mutationObserver.observe(strip, { childList: true });
  strip.addEventListener('scroll', schedule, { passive: true });
  schedule();
}

function setFlag(element: HTMLElement, key: 'overflowStart' | 'overflowEnd', on: boolean): void {
  if (on) {
    if (element.dataset[key] !== 'true') element.dataset[key] = 'true';
  } else if (key in element.dataset) {
    delete element.dataset[key];
  }
}

function scrollActiveTabIntoView(strip: HTMLElement, fallback?: HTMLElement): void {
  if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 720px)').matches) return;
  const scroller = strip.scrollWidth > strip.clientWidth
    ? strip
    : strip.parentElement?.closest<HTMLElement>(TAB_SCROLLER_SELECTOR);
  if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

  const active = strip.querySelector<HTMLElement>(
    '[aria-selected="true"], [data-active="true"], .active, .is-active',
  ) ?? fallback;
  if (!active || typeof active.scrollIntoView !== 'function') return;
  active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
}
